import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { ReasoningEffort } from "@/types/chat.type";
import { REASONING_LABELS } from "@/app/routes/dashboard/components/agentChatUi";

/** Thinking effort as a stepped slider. The chosen stop is named on the
 *  "Thinking effort" row, not under the track. */
export function EffortSlider({
  options,
  value,
  onChange,
  labelledBy,
  startDelay = 0,
}: {
  /** ms to wait before the thumb sweeps from 0 to the chosen stop on mount. */
  startDelay?: number;
  options: ReasoningEffort[];
  value: ReasoningEffort;
  onChange: (effort: ReasoningEffort) => void;
  labelledBy: string;
}) {
  const count = options.length;
  const index = Math.max(0, options.indexOf(value));
  const last = Math.max(count - 1, 1);
  const stepP = count <= 1 ? 0 : index / (count - 1);
  const stop = (i: number) => ({ "--p": count <= 1 ? 0 : i / (count - 1) }) as CSSProperties;
  const prev = useRef(index);
  const latestIndexRef = useRef(index);
  latestIndexRef.current = index;
  const impactTimersRef = useRef<number[]>([]);
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  const dragPointerId = useRef<number | null>(null);
  const [dragP, setDragP] = useState<number | null>(null);
  const [pop, setPop] = useState<{ i: number; n: number } | null>(null);
  const [thumbPopOn, setThumbPopOn] = useState(false);
  const [shock, setShock] = useState(0);
  const [shockOn, setShockOn] = useState(false);
  // On mount the thumb sweeps from 0 to the chosen stop (once, after
  // startDelay), so the fill and thumb start empty. Reduced motion, or a stop
  // at 0, has nothing to sweep.
  const [sweep] = useState(
    () => index > 0 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [armed, setArmed] = useState(!sweep);
  const [arriving, setArriving] = useState(sweep);
  // The thumb eases into the stop, so the dot stays up until that move ends.
  const [covered, setCovered] = useState(sweep ? 0 : index);

  const p = armed ? (dragP ?? stepP) : 0;
  const isMax = armed && count > 1 && index === count - 1;

  useEffect(() => {
    if (armed) return;
    const t = window.setTimeout(() => setArmed(true), startDelay);
    return () => window.clearTimeout(t);
  }, [armed, startDelay]);

  // Sweeping up to the top stop lands with the same pop and shockwave as
  // dragging there, timed to the end of the sweep.
  useEffect(() => {
    if (!armed || !sweep || count < 2 || latestIndexRef.current !== count - 1) return;
    triggerMaxImpact(500);
    // Once, when the sweep starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed]);

  useEffect(() => {
    if (!armed || !arriving) return;
    const t = window.setTimeout(() => setArriving(false), 800);
    return () => window.clearTimeout(t);
  }, [armed, arriving]);

  const clearImpactTimers = () => {
    impactTimersRef.current.forEach((id) => window.clearTimeout(id));
    impactTimersRef.current = [];
  };

  const triggerMaxImpact = (leadDelayMs = 0) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    clearImpactTimers();
    setShockOn(false);

    const startPop = () => {
      setThumbPopOn(true);
      // Fire shockwave at 280ms when the handle hits its smallest size (55% of 520ms)
      const shockStart = window.setTimeout(() => {
        setShock((n) => n + 1);
        setShockOn(true);
      }, 280);
      const popEnd = window.setTimeout(() => {
        setThumbPopOn(false);
      }, 520);
      const shockEnd = window.setTimeout(() => {
        setShockOn(false);
      }, 1040);
      impactTimersRef.current.push(shockStart, popEnd, shockEnd);
    };

    if (leadDelayMs <= 0) {
      startPop();
    } else {
      setThumbPopOn(false);
      const lead = window.setTimeout(startPop, leadDelayMs);
      impactTimersRef.current.push(lead);
    }
  };

  useEffect(() => clearImpactTimers, []);

  useEffect(() => {
    if (prev.current === index) return;
    const from = prev.current;
    prev.current = index;
    setPop({ i: from < index ? from : index, n: Date.now() });
    if (dragPointerId.current === null && index === count - 1 && count > 1 && from < index) {
      triggerMaxImpact(220);
    }
  }, [index, count]);

  useEffect(() => {
    if (!armed || covered === index) return;
    const thumb = thumbRef.current;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!thumb || reduce) {
      setCovered(index);
      return;
    }
    const finish = (event: TransitionEvent) => {
      if (event.propertyName !== "inset-inline-start" && event.propertyName !== "left") return;
      setCovered(index);
    };
    const timer = window.setTimeout(() => setCovered(index), arriving ? 900 : 400);
    thumb.addEventListener("transitionend", finish);
    return () => {
      thumb.removeEventListener("transitionend", finish);
      window.clearTimeout(timer);
    };
  }, [index, covered, armed, arriving]);

  const computeDragP = useRef<(clientX: number) => number>(() => stepP);
  computeDragP.current = (clientX: number) => {
    const el = sliderRef.current;
    if (!el || count <= 1) return stepP;
    const rect = el.getBoundingClientRect();
    const thumbSize = rect.height || 26;
    const travel = Math.max(1, rect.width - thumbSize);
    const raw = Math.min(1, Math.max(0, (clientX - rect.left - thumbSize / 2) / travel));
    const nearestIndex = Math.round(raw * (count - 1));
    const nearestStop = nearestIndex / (count - 1);
    const dist = raw - nearestStop;
    const magnetRadius = 0.32 / (count - 1);
    // Subtle magnetic pull toward each stop while scrubbing
    const pulled =
      Math.abs(dist) < magnetRadius
        ? nearestStop + dist * 0.45
        : raw;
    latestIndexRef.current = nearestIndex;
    const nextOption = options[nearestIndex];
    if (nextOption && nextOption !== value) {
      onChange(nextOption);
    }
    return pulled;
  };

  const isDragging = dragP !== null;

  useEffect(() => {
    if (!isDragging) return;
    const endDrag = () => {
      const landedMax = count > 1 && latestIndexRef.current === count - 1;
      dragPointerId.current = null;
      setDragP(null);
      if (landedMax) {
        triggerMaxImpact(0);
      }
    };
    const handleMove = (e: PointerEvent) => {
      if (dragPointerId.current !== null && e.pointerId !== dragPointerId.current) return;
      if (e.pointerType === "mouse" && (e.buttons & 1) === 0) {
        endDrag();
        return;
      }
      setDragP(computeDragP.current(e.clientX));
    };
    const handleUp = (e: PointerEvent) => {
      if (dragPointerId.current !== null && e.pointerId !== dragPointerId.current) return;
      endDrag();
    };
    window.addEventListener("pointermove", handleMove, true);
    window.addEventListener("pointerup", handleUp, true);
    window.addEventListener("pointercancel", handleUp, true);
    window.addEventListener("blur", endDrag);
    return () => {
      window.removeEventListener("pointermove", handleMove, true);
      window.removeEventListener("pointerup", handleUp, true);
      window.removeEventListener("pointercancel", handleUp, true);
      window.removeEventListener("blur", endDrag);
    };
  }, [isDragging, count]);

  return (
    <div
      className="m7r-effort"
      data-shock={shockOn ? "" : undefined}
      data-max={isMax ? "" : undefined}
      data-dragging={isDragging ? "" : undefined}
      data-arriving={arriving && armed ? "" : undefined}
      style={{ "--p": p } as CSSProperties}
    >
      <div ref={sliderRef} className="m7r-effort-slider">
        <div className="m7r-effort-rail" aria-hidden="true">
          <div className="m7r-effort-fill">
            {shock > 0 && <span key={shock} className="m7r-effort-ripple" />}
          </div>
          {options.map((option, i) => (
            <span
              key={pop?.i === i ? `${option}-${pop.n}` : option}
              className="m7r-effort-stop"
              data-on={i / Math.max(count - 1, 1) < p - 0.02 ? "true" : undefined}
              data-current={i === index && i === covered && !isDragging ? "true" : undefined}
              data-pop={pop?.i === i ? "true" : undefined}
              style={stop(i)}
            />
          ))}
        </div>
        <span
          ref={thumbRef}
          className="m7r-effort-thumb"
          data-pop={thumbPopOn ? "" : undefined}
          aria-hidden="true"
        />
        <input
          type="range"
          min={0}
          max={Math.max(count - 1, 0)}
          step={1}
          value={count ? Math.min(index, last) : 0}
          disabled={count < 2}
          aria-labelledby={labelledBy}
          aria-valuetext={REASONING_LABELS[value] ?? value}
          onPointerDown={(e) => {
            if (count < 2 || (e.pointerType === "mouse" && e.button !== 0)) return;
            clearImpactTimers();
            setThumbPopOn(false);
            dragPointerId.current = e.pointerId;
            setDragP(computeDragP.current(e.clientX));
          }}
          onChange={(e) => {
            if (dragPointerId.current !== null) return;
            const next = options[Number(e.target.value)];
            if (next) onChange(next);
          }}
        />
      </div>
    </div>
  );
}
